import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { FaArrowDown, FaArrowUp, FaPencilAlt } from 'react-icons/fa'
import { IoIosRepeat } from 'react-icons/io'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent'
import NoRecordFound from '../../../components/noRecord/NoRecordFound'
import Pagination from '../../../components/pagination/Pagination'
import SearchBarComponent from '../../../components/searchBar/SearchBarComponent'
import SearchSelect from '../../../components/textFields/SearchSelect'
import TextField from '../../../components/textFields/TextField'
import DateRangeComp from '../../../components/dateComponent/DateRangeComp'
import TableHeading from '../../../components/tableHeading/TableHeading'
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu'
import { useGetAllUserDraftQuery } from '../../../services/DraftApi'
import { useGetAllDivisionQuery, useDepartmentsLookupQuery, useGetAllDesignationWithoutRoleQuery, useGetAllLocationsQuery } from '../../../services/GeneralApi'
import labelKey from "../../localization/label.json"
import UpdateUserStatusModal from './UpdateUserStatusModal'
import OnboardingUserRequestDetailModal from './OnboardingUserRequestDetailModal'
import OnboardingUserRequestEditModal from './OnboardingUserRequestEditModal'
import DateTimeComp from '../../../components/dateComponent/DateTimeComp'
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp'
import { FaCheck, FaEye } from 'react-icons/fa6'
import { Link } from 'react-router-dom'
import { constraintConfig } from '../../../constraintConfig'
import { useAppSelector } from '../../../hooks'
import { selectAuth } from '../../../features/authSlice'
import { KTSVG } from '../../../_metronic/helpers'
import SsRowActions from '../../../components/tableActions/SsRowActions'
import ReactReadMoreReadLess from "react-read-more-read-less"
import useColumnVisibility from '../../customHooks/useColumnVisibility'
import RefreshComponent from '../../../components/refreshComponent/RefreshComponent'

const CC_REQUEST_OPTIONS = [
    { value: '0', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '2', label: 'No' },
]

const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: constraintConfig.newUserRequestStatus.Pending, label: labelKey.pendingForApproval },
    { value: constraintConfig.newUserRequestStatus.Approved, label: 'Approved' },
    { value: constraintConfig.newUserRequestStatus.Rejected, label: 'Rejected' },
]

const lookupOptionLabel = (options: { value: string; label: string }[], id: any, fallbackId?: any) => {
    const raw = id != null && id !== '' ? id : fallbackId
    if (raw == null || raw === '' || Number(raw) === 0) return 'N/A'
    const key = String(raw)
    return options.find(o => o.value === key)?.label || key
}

const displayManager = (row: any) => {
    const name = row?.managerName?.trim()
    const email = row?.managerEmail?.trim()
    if (name && email) return `${name} (${email})`
    if (name) return name
    if (email) return email
    return 'N/A'
}

const displayPhone = (row: any) => {
    const phone = (row?.cellPhone || row?.businessPhone || '').toString().trim()
    if (!phone || /^[\s_\-]+$/.test(phone)) return 'N/A'
    return phone
}

// Column configuration
interface ColumnConfig {
    id: string;
    label: string;
    sortable: boolean;
    dataKey: string;
}

const AccountManager = ({ tabChange }: any) => {
    const { roleID, isTravelModuleAccessAvailable } = useAppSelector(selectAuth)
    const isOrgAdmin = roleID === constraintConfig.roleID.role2
        || roleID === constraintConfig.roleID.role1
        || roleID === constraintConfig.roleID.roleIDAdmin

    const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'onboardingUserRequests' })
    const { data: divisionLookup } = useGetAllDivisionQuery('')
    const { data: departmentLookup } = useDepartmentsLookupQuery('')
    const { data: designationLookup } = useGetAllDesignationWithoutRoleQuery('')
    const { data: locationsData } = useGetAllLocationsQuery('')

    // Dynamic column configuration
    const COLUMN_CONFIG = useMemo(() => {
        const columns: ColumnConfig[] = [
            { id: 'sr', label: labelKey.sr, sortable: false, dataKey: 'sr' },
            { id: 'firstName', label: labelKey.user, sortable: true, dataKey: 'firstName' },
            { id: 'managerEmail', label: 'Manager', sortable: false, dataKey: 'managerEmail' },
            { id: 'cellPhone', label: 'Phone', sortable: false, dataKey: 'cellPhone' },
            { id: 'division', label: 'Division', sortable: false, dataKey: 'division' },
            { id: 'department', label: labelKey.department, sortable: false, dataKey: 'department' },
            { id: 'designation', label: 'Designation', sortable: false, dataKey: 'designation' },
            { id: 'dayforceId', label: 'Dayforce ID', sortable: false, dataKey: 'dayforceId' },
            { id: 'findAddress', label: 'Find Address', sortable: false, dataKey: 'findAddress' },
            { id: 'location', label: 'Office Location', sortable: false, dataKey: 'location' },
            { id: 'apiOrgName', label: labelKey.organizationName, sortable: true, dataKey: 'apiOrgName' },
            { id: 'status', label: 'User Status', sortable: true, dataKey: 'mgrActionStatus' },
            { id: 'comments', label: labelKey.comments, sortable: false, dataKey: 'comments' },
            { id: 'createdDT', label: 'Corp Requested Date', sortable: true, dataKey: 'createdDT' },
            { id: 'modifiedOn', label: 'Modified Date', sortable: false, dataKey: 'modifiedOn' },
            { id: 'approvelDT', label: 'Approved Date', sortable: true, dataKey: 'approvelDT' },
        ];

        if (isTravelModuleAccessAvailable) {
            columns.push({ id: 'requestTravelPortalAccess', label: 'Travel Portal Access', sortable: false, dataKey: 'requestTravelPortalAccess' });
        }

        columns.push(
            { id: 'requestCorporateCreaditCard', label: 'Corporate CC Requested', sortable: false, dataKey: 'requestCorporateCreaditCard' },
            { id: 'spendingLimit', label: 'Corp CC Limit', sortable: false, dataKey: 'spendingLimit' },
            { id: 'actions', label: labelKey.action, sortable: false, dataKey: 'actions' }
        );

        return columns;
    }, [isTravelModuleAccessAvailable]);

    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [searchStr, setSearchStr] = useState('')
    const [sortBy, setSortBy] = useState('createdDT')
    const [sortOrder, setSortOrder] = useState('desc')
    const [sortedColumn, setSortedColumn] = useState('createdDT')
    const [filterByStatus, setFilterByStatus] = useState(constraintConfig.newUserRequestStatus.Pending)
    const [filterByCcRequest, setFilterByCcRequest] = useState('0')
    const [filterByUserName, setFilterByUserName] = useState('')
    const [filterByEmail, setFilterByEmail] = useState('')
    const [filterByOrgName, setFilterByOrgName] = useState('')
    const [filterByDivisionId, setFilterByDivisionId] = useState('')
    const [filterByDepartmentId, setFilterByDepartmentId] = useState('')
    const [filterByLocationId, setFilterByLocationId] = useState('')
    const [filterByPhone, setFilterByPhone] = useState('')
    const [requestedFrom, setRequestedFrom] = useState('')
    const [requestedTo, setRequestedTo] = useState('')
    const [modifiedFrom, setModifiedFrom] = useState('')
    const [modifiedTo, setModifiedTo] = useState('')
    const [approvedFrom, setApprovedFrom] = useState('')
    const [approvedTo, setApprovedTo] = useState('')
    const [activeTab, setActiveTab] = useState(constraintConfig.newUserRequestStatus.Pending)
    const [showFilters, setShowFilters] = useState(false)

    const divisionOptions = useMemo(() => {
        const list = Array.isArray(divisionLookup?.result)
            ? divisionLookup.result
            : Array.isArray(divisionLookup)
                ? divisionLookup
                : [];
        return [
            { value: '', label: 'All Divisions' },
            ...list.map((d: any) => ({
                value: String(d?.id ?? d?.value ?? ''),
                label: d?.value || d?.title || String(d?.id ?? ''),
            })),
        ];
    }, [divisionLookup])

    const departmentOptions = useMemo(() => {
        const list = Array.isArray(departmentLookup?.result)
            ? departmentLookup.result
            : Array.isArray(departmentLookup)
                ? departmentLookup
                : [];
        return [
            { value: '', label: 'All Departments' },
            ...list.map((d: any) => ({
                value: String(d?.id ?? d?.value ?? ''),
                label: d?.value || d?.title || String(d?.id ?? ''),
            })),
        ];
    }, [departmentLookup])

    const designationOptions = useMemo(() => {
        const list = Array.isArray(designationLookup?.result)
            ? designationLookup.result
            : Array.isArray(designationLookup)
                ? designationLookup
                : [];
        return [
            { value: '', label: 'All Designations' },
            ...list.map((d: any) => ({
                value: String(d?.id ?? d?.value ?? ''),
                label: d?.value || d?.title || String(d?.id ?? ''),
            })),
        ];
    }, [designationLookup])

    const locationOptions = useMemo(() => {
        const list = Array.isArray(locationsData?.result)
            ? locationsData.result
            : Array.isArray(locationsData)
                ? locationsData
                : [];
        return [
            { value: '', label: 'All Locations' },
            ...list.map((d: any) => ({
                value: String(d?.id ?? d?.value ?? ''),
                label: d?.value || d?.title || String(d?.id ?? ''),
            })),
        ];
    }, [locationsData])

    const { data, isFetching: isLoading, isSuccess, refetch } = useGetAllUserDraftQuery({
        pageNumber: page,
        pageSize,
        sortBy,
        sortOrder,
        searchStr,
        filterByStatus: filterByStatus || 0,
        filterByCcRequest: Number(filterByCcRequest) || 0,
        filterByUserName: filterByUserName || undefined,
        filterByEmail: filterByEmail || undefined,
        filterByOrgName: filterByOrgName || undefined,
        filterByDivisionId: filterByDivisionId ? Number(filterByDivisionId) : undefined,
        filterByDepartmentId: filterByDepartmentId ? Number(filterByDepartmentId) : undefined,
        filterByLocationId: filterByLocationId ? Number(filterByLocationId) : undefined,
        filterByPhone: filterByPhone || undefined,
        requestedFrom: requestedFrom || undefined,
        requestedTo: requestedTo || undefined,
        modifiedFrom: modifiedFrom || undefined,
        modifiedTo: modifiedTo || undefined,
        approvedFrom: approvedFrom || undefined,
        approvedTo: approvedTo || undefined,
    })

    const [showConfirmationActiveUserModal, setShowConfirmationActiveUserModal] = useState(false)
    const [editData, setEditData] = useState({})
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [detailData, setDetailData] = useState<any>(null)
    const [showEditModal, setShowEditModal] = useState(false)
    const [editRequestData, setEditRequestData] = useState<any>(null)

    const handlePageSizeChange = (e: any) => {
        setPageSize(parseInt(e.target.value))
        setPage(1)
    }

    const handlePageClick = (e: any) => {
        setPage(e.selected + 1)
    }

    const handleSearchChange = (e: any) => {
        setSearchStr(e.target.value)
        setPage(1)
    }

    const handleSort = (property: string) => {
        setSortBy(property)
        if (sortedColumn === property) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortedColumn(property)
            setSortOrder('asc')
        }
    }

    useEffect(() => {
        refetch()
    }, [tabChange, showConfirmationActiveUserModal, showEditModal])

    useEffect(() => {
        setPage(1)
    }, [
        searchStr, filterByStatus, filterByCcRequest, filterByUserName, filterByEmail, filterByOrgName,
        filterByDivisionId, filterByDepartmentId, filterByLocationId, filterByPhone, requestedFrom, requestedTo,
        modifiedFrom, modifiedTo, approvedFrom, approvedTo,
    ])

    const handleTabChange = (status: string) => {
        setActiveTab(status)
        setFilterByStatus(status)
        setPage(1)
    }

    const handleRefresh = () => {
        refetch().then(() => {
            if (isSuccess) toast.success('Data Refresh successfully!')
        })
    }

    const clearFilters = () => {
        setFilterByCcRequest('0')
        setFilterByUserName('')
        setFilterByEmail('')
        setFilterByOrgName('')
        setFilterByDivisionId('')
        setFilterByDepartmentId('')
        setFilterByLocationId('')
        setFilterByPhone('')
        setRequestedFrom('')
        setRequestedTo('')
        setModifiedFrom('')
        setModifiedTo('')
        setApprovedFrom('')
        setApprovedTo('')
        setSearchStr('')
        setPage(1)
    }

    const openDetail = (row: any) => {
        setDetailData({ userDraftID: row?.userDraftID || row?.id })
        setShowDetailModal(true)
    }

    const openEdit = (row: any) => {
        setEditRequestData({ userDraftID: row?.userDraftID || row?.id })
        setShowEditModal(true)
    }

    const isColumnVisible = (columnId: string) => hiddenColumns.includes(columnId)

    // Get visible columns (excluding actions for the main table body)
    const visibleColumns = COLUMN_CONFIG.filter(col => isColumnVisible(col.id) && col.id !== 'actions');
    const showActions = isColumnVisible('actions');

    // Render cell function - now accepts index as parameter
    const renderCell = (row: any, columnId: string, index: number) => {
        switch (columnId) {
            case 'sr':
                return (page - 1) * pageSize + index + 1;
            case 'firstName':
                return (
                    <div className="d-flex align-items-center">
                        <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                            <div className="symbol-label fs-3 bg-light-info text-info text-capitalize">{row?.firstName?.charAt(0)}</div>
                        </div>
                        <div className="d-flex flex-column">
                            <span className="text-gray-800 fw-bold mb-1 text-capitalize">{row?.firstName} {row?.lastName}</span>
                            <span className='text-lowercase'>{row?.userEmail}</span>
                        </div>
                    </div>
                );
            case 'managerEmail':
                return displayManager(row);
            case 'cellPhone':
                return displayPhone(row);
            case 'division':
                return lookupOptionLabel(divisionOptions, row?.division?.id, row?.divisionId);
            case 'department':
                return lookupOptionLabel(departmentOptions, row?.department?.id, row?.departmentId);
            case 'designation':
                return lookupOptionLabel(designationOptions, row?.designation?.id, row?.designationId);
            case 'dayforceId':
                return row?.dayforceId || 'N/A';
            case 'findAddress':
                return <span className='text-wrap' style={{ maxWidth: 220 }}>{row?.findAddress || 'N/A'}</span>;
            case 'location':
                return lookupOptionLabel(locationOptions, row?.location?.id, row?.locationId);
            case 'apiOrgName':
                return row?.apiOrgName;
            case 'status':
                return (
                    row?.userApprovalStatus?.id == constraintConfig.newUserRequestStatus.Approved ? (
                        <span className="badge badge-light-success fs-7 fw-semibold">{labelKey.approved}</span>
                    ) : row?.userApprovalStatus?.id == constraintConfig.newUserRequestStatus.Pending ? (
                        <span className="badge badge-light-info fs-7 fw-semibold">{labelKey.pendingForApproval}</span>
                    ) : (
                        <span className="badge badge-danger fs-7 fw-semibold">{labelKey.rejected}</span>
                    )
                );
            case 'comments':
                return row?.comments ? (
                    <ReactReadMoreReadLess
                        charLimit={30}
                        readMoreText={"Read more"}
                        readLessText={"Read less"}
                        readMoreClassName="readMore"
                        readLessClassName="readLess"
                    >
                        {row.comments}
                    </ReactReadMoreReadLess>
                ) : 'N/A';
            case 'createdDT':
                return <DateTimeComp formattedDate={row?.createdDT} />;
            case 'modifiedOn':
                return <DateTimeComp formattedDate={row?.modifiedOn || row?.createdDT} />;
            case 'approvelDT':
                return <DateTimeComp formattedDate={row?.approvelDT} />;
            case 'requestTravelPortalAccess':
                return row?.requestTravelPortalAccess ? 'Yes' : 'No';
            case 'requestCorporateCreaditCard':
                return <span className='text-center'>{row?.requestCorporateCreaditCard ? 'Yes' : 'No'}</span>;
            case 'spendingLimit':
                return row?.requestCorporateCreaditCard
                    ? (row?.spendingLimit != null && Number(row.spendingLimit) > 0
                        ? <CurrencyComp amount={row.spendingLimit} />
                        : 'N/A')
                    : 'N/A';
            default:
                return row?.[columnId] ?? '';
        }
    }

    const visibleColCount = COLUMN_CONFIG.filter(col => isColumnVisible(col.id)).length || 1

    return (
        <>
            <UpdateUserStatusModal
                show={showConfirmationActiveUserModal}
                handleClose={() => setShowConfirmationActiveUserModal(false)}
                data={editData}
            />
            <OnboardingUserRequestDetailModal
                show={showDetailModal}
                handleClose={() => {
                    setShowDetailModal(false)
                    setDetailData(null)
                }}
                data={detailData}
            />
            <OnboardingUserRequestEditModal
                show={showEditModal}
                handleClose={() => {
                    setShowEditModal(false)
                    setEditRequestData(null)
                }}
                data={editRequestData}
                onSaved={() => refetch()}
            />
            <Tooltip id="refreshData" place="top" />
            <Tooltip id="viewDetail" place="left" positionStrategy="fixed" />
            <Tooltip id="editRequest" place="left" positionStrategy="fixed" />
            <Tooltip id="Approve-Status" place="left" positionStrategy="fixed" />

            <div className='card'>
                <div className='card-header border-0 align-items-center flex-wrap gap-2 pt-5'>
                        <Alert variant="primary" className="mb-3 ss-page-info-alert ss-page-info-alert--full">
                            <div className="ss-page-info-alert__body">
                                <p className='fs-7 mb-0'>
                                    Onboarding user requests awaiting manager action. After approval, the user account is created. If a corporate credit card was requested, a CC request is opened and routed to higher management by division; otherwise higher management receives a registration notice.
                                </p>
                            </div>
                        </Alert>

                        <div className='d-flex flex-column flex-lg-row flex-wrap align-items-stretch align-items-lg-center justify-content-between gap-2 w-100 ss-onboarding-toolbar ss-receipt-vault-toolbar'>
                            <div className='d-flex gap-2 align-items-center min-w-0 w-100 w-lg-auto ss-receipt-vault-toolbar__tabs'>
                                <div className='ss-mobile-scroll-tabs flex-grow-1 min-w-0'>
                                <div className="ss-user-tab-group btn-group" role="group" aria-label="Onboarding status tabs" style={{ minWidth: 'max-content' }}>
                                    <button
                                        type='button'
                                        className={`btn btn-sm text-nowrap ${activeTab == constraintConfig.newUserRequestStatus.Pending ? 'btn-primary' : 'btn-light-primary'}`}
                                        onClick={() => handleTabChange(constraintConfig.newUserRequestStatus.Pending)}
                                    >
                                        {labelKey.pendingForApproval}
                                    </button>
                                    <button
                                        type='button'
                                        className={`btn btn-sm text-nowrap ${activeTab === constraintConfig.newUserRequestStatus.Approved ? 'btn-primary' : 'btn-light-primary'}`}
                                        onClick={() => handleTabChange(constraintConfig.newUserRequestStatus.Approved)}
                                    >
                                        Approved Users
                                    </button>
                                    <button
                                        type='button'
                                        className={`btn btn-sm text-nowrap ${activeTab === constraintConfig.newUserRequestStatus.Rejected ? 'btn-primary' : 'btn-light-primary'}`}
                                        onClick={() => handleTabChange(constraintConfig.newUserRequestStatus.Rejected)}
                                    >
                                        Rejected Users
                                    </button>
                                </div>
                                </div>
                            </div>

                            <div className='ss-mobile-scroll-tabs w-100 w-lg-auto ss-receipt-vault-toolbar__actions ms-lg-auto'>
                            <div className='d-flex justify-content-start align-items-center flex-nowrap gap-2 ms-lg-auto ss-user-list-toolbar__actions' data-kt-user-table-toolbar='base' style={{ minWidth: 'max-content' }}>
                                <SearchBarComponent
                                    placeholder='User'
                                    value={searchStr}
                                    onChange={handleSearchChange}
                                    className='ss-user-toolbar-search-input'
                                    mainClassName='ss-table-search--sm my-0'
                                />
                                <RefreshComponent onClick={handleRefresh} />
                                {!showFilters ? (
                                    <button
                                        type="button"
                                        className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active"
                                        onClick={() => setShowFilters(true)}
                                    >
                                        <i className="fa fa-filter me-1 text-white" />
                                        Show Filters
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active"
                                        onClick={() => setShowFilters(false)}
                                    >
                                        <i className="fa fa-eye-slash me-1 text-white" />
                                        Hide Filters
                                    </button>
                                )}
                                {(roleID === constraintConfig.roleID.role2) && (
                                    <Link to='/newuserrequest' className='btn btn-sm ss-pill-btn ss-pill-btn-filter-active'>
                                        <i className="fa fa-plus me-1 text-white" />
                                        New User Request
                                    </Link>
                                )}
                                {(roleID === constraintConfig.roleID.role4) && (
                                    <Link to='/newuserrequestbymanager' className='btn btn-sm ss-pill-btn ss-pill-btn-filter-active'>
                                        <i className="fa fa-plus me-1 text-white" />
                                        New User Request
                                    </Link>
                                )}
                            </div>
                            </div>
                        </div>
                    </div>

                    <div className='card-body py-3'>
                        {showFilters && (
                            <div className='ss-user-list-filters mb-3'>
                                <div className='row g-2 ss-listing-filters align-items-end'>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-3'>
                                        <label className='form-label ss-compact-filter-label'>User Name</label>
                                        <TextField
                                            placeholder='Name'
                                            value={filterByUserName}
                                            onChange={(e: any) => setFilterByUserName(e.target.value)}
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>Email</label>
                                        <TextField
                                            placeholder='Email'
                                            value={filterByEmail}
                                            onChange={(e: any) => setFilterByEmail(e.target.value)}
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>Phone</label>
                                        <TextField
                                            placeholder='Phone'
                                            value={filterByPhone}
                                            onChange={(e: any) => setFilterByPhone(e.target.value)}
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>Org Name</label>
                                        <TextField
                                            placeholder='Organization'
                                            value={filterByOrgName}
                                            onChange={(e: any) => setFilterByOrgName(e.target.value)}
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>User Request Status</label>
                                        <SearchSelect
                                            selectClass='filter-valueContainer ss-compact-select'
                                            options={STATUS_OPTIONS}
                                            value={STATUS_OPTIONS.find(o => o.value === String(filterByStatus)) || STATUS_OPTIONS[0]}
                                            onChange={(e: any) => {
                                                const val = e?.value != null ? String(e.value) : ''
                                                setFilterByStatus(val)
                                                setActiveTab(val || constraintConfig.newUserRequestStatus.Pending)
                                                setPage(1)
                                            }}
                                            placeholder='All Statuses'
                                            isClearable
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>Division</label>
                                        <SearchSelect
                                            selectClass='filter-valueContainer ss-compact-select'
                                            options={divisionOptions}
                                            value={divisionOptions.find(o => o.value === filterByDivisionId) || divisionOptions[0]}
                                            onChange={(e: any) => setFilterByDivisionId(e?.value != null ? String(e.value) : '')}
                                            placeholder='All Divisions'
                                            isClearable
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>Department</label>
                                        <SearchSelect
                                            selectClass='filter-valueContainer ss-compact-select'
                                            options={departmentOptions}
                                            value={departmentOptions.find(o => o.value === filterByDepartmentId) || departmentOptions[0]}
                                            onChange={(e: any) => setFilterByDepartmentId(e?.value != null ? String(e.value) : '')}
                                            placeholder='All Departments'
                                            isClearable
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>Office Location</label>
                                        <SearchSelect
                                            selectClass='filter-valueContainer ss-compact-select'
                                            options={locationOptions}
                                            value={locationOptions.find(o => o.value === filterByLocationId) || locationOptions[0]}
                                            onChange={(e: any) => setFilterByLocationId(e?.value != null ? String(e.value) : '')}
                                            placeholder='All Locations'
                                            isClearable
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>Corporate Credit Card Requested</label>
                                        <SearchSelect
                                            selectClass='filter-valueContainer ss-compact-select'
                                            options={CC_REQUEST_OPTIONS}
                                            value={CC_REQUEST_OPTIONS.find(o => o.value === filterByCcRequest) || CC_REQUEST_OPTIONS[0]}
                                            onChange={(e: any) => setFilterByCcRequest(e?.value != null ? String(e.value) : '0')}
                                            placeholder='All'
                                            isClearable
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <DateRangeComp
                                            label='Requested Date Range'
                                            startDate={requestedFrom ? moment(requestedFrom) : moment().subtract(30, 'days')}
                                            endDate={requestedTo ? moment(requestedTo) : moment()}
                                            onApply={(_: any, picker: any) => {
                                                setRequestedFrom(moment(picker.startDate).format('YYYY-MM-DD'))
                                                setRequestedTo(moment(picker.endDate).format('YYYY-MM-DD'))
                                            }}
                                            onCancel={() => { setRequestedFrom(''); setRequestedTo('') }}
                                            value={requestedFrom && requestedTo ? `${moment(requestedFrom).format('MM/DD/YYYY')} - ${moment(requestedTo).format('MM/DD/YYYY')}` : 'MM/DD/YYYY'}
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <DateRangeComp
                                            label='Modified Date Range'
                                            startDate={modifiedFrom ? moment(modifiedFrom) : moment().subtract(30, 'days')}
                                            endDate={modifiedTo ? moment(modifiedTo) : moment()}
                                            onApply={(_: any, picker: any) => {
                                                setModifiedFrom(moment(picker.startDate).format('YYYY-MM-DD'))
                                                setModifiedTo(moment(picker.endDate).format('YYYY-MM-DD'))
                                            }}
                                            onCancel={() => { setModifiedFrom(''); setModifiedTo('') }}
                                            value={modifiedFrom && modifiedTo ? `${moment(modifiedFrom).format('MM/DD/YYYY')} - ${moment(modifiedTo).format('MM/DD/YYYY')}` : 'MM/DD/YYYY'}
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-3 col-xl-2'>
                                        <DateRangeComp
                                            label='Approved Date Range'
                                            startDate={approvedFrom ? moment(approvedFrom) : moment().subtract(30, 'days')}
                                            endDate={approvedTo ? moment(approvedTo) : moment()}
                                            onApply={(_: any, picker: any) => {
                                                setApprovedFrom(moment(picker.startDate).format('YYYY-MM-DD'))
                                                setApprovedTo(moment(picker.endDate).format('YYYY-MM-DD'))
                                            }}
                                            onCancel={() => { setApprovedFrom(''); setApprovedTo('') }}
                                            value={approvedFrom && approvedTo ? `${moment(approvedFrom).format('MM/DD/YYYY')} - ${moment(approvedTo).format('MM/DD/YYYY')}` : 'MM/DD/YYYY'}
                                        />
                                    </div>
                                    <div className='col-12 col-sm-6 col-lg-auto'>
                                        <div className='d-flex align-items-end h-100 gap-2 pb-0 ss-listing-filter-actions'>
                                            <button type='button' className='btn btn-sm btn-danger ss-smart-btn text-nowrap' onClick={clearFilters}>
                                                Reset
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className='table-responsive table-height'>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                                <thead>
                                    <tr className='fw-bold text-muted'>
                                        {visibleColumns.map((col) => (
                                            <TableHeading
                                                key={col.id}
                                                label={col.label}
                                                columnId={col.id}
                                                sortedColumn={col.sortable ? sortedColumn : undefined}
                                                sortOrder={col.sortable ? sortOrder : undefined}
                                                handleSort={col.sortable ? handleSort : undefined}
                                                className={col.id === 'sr' ? 'min-w-40px ps-4' : 'min-w-120px'}
                                            />
                                        ))}
                                        {showActions && (
                                            <th className='text-end rounded-end pe-2 border-0 sticky-table-actions ss-smart-sticky-actions ss-user-list-actions-col'>
                                                <div className='d-flex align-items-center justify-content-end ss-row-actions-header'>
                                                    <span className='d-none d-xl-inline'>{labelKey.actions}</span>
                                                    <TableSettingMenu>
                                                        {COLUMN_CONFIG.map((col) => (
                                                            <TextField
                                                                key={col.id}
                                                                rightLabel={col.label}
                                                                type="checkbox"
                                                                onChange={() => handleToggleColumn(col.id)}
                                                                checked={isColumnVisible(col.id)}
                                                                fieldClass='mb-4'
                                                            />
                                                        ))}
                                                    </TableSettingMenu>
                                                </div>
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {!isLoading && (
                                        <>
                                            {data?.result?.data?.length > 0 ? (
                                                data?.result?.data?.map((row: any, index: number) => (
                                                    <tr key={row.userDraftID || row.id || index}>
                                                        {visibleColumns.map((col) => (
                                                            <td key={col.id} className={col.id === 'sr' ? 'ps-4' : ''}>
                                                                {renderCell(row, col.id, index)}
                                                            </td>
                                                        ))}
                                                        {showActions && (
                                                            <td className='text-end sticky-table-actions ss-user-list-actions-col' onClick={(e) => e.stopPropagation()}>
                                                                <SsRowActions
                                                                    menuId={`onboard-row-actions-${row.userDraftID || row.id || index}`}
                                                                    actions={[
                                                                        {
                                                                            key: 'view-detail',
                                                                            label: 'View Detail',
                                                                            tooltipId: 'viewDetail',
                                                                            icon: <FaEye className='h-14px w-14px' />,
                                                                            onClick: () => openDetail(row),
                                                                        },
                                                                        {
                                                                            key: 'edit-request',
                                                                            label: 'Edit Request Details',
                                                                            hidden: !(isOrgAdmin && String(row?.userApprovalStatus?.id) === constraintConfig.newUserRequestStatus.Pending),
                                                                            tooltipId: 'editRequest',
                                                                            icon: <FaPencilAlt className='h-14px w-14px' />,
                                                                            onClick: () => openEdit(row),
                                                                        },
                                                                        {
                                                                            key: 'update-status',
                                                                            label: labelKey.updateUserStatus,
                                                                            tooltipId: 'Approve-Status',
                                                                            icon: <FaCheck className='h-14px w-14px' />,
                                                                            onClick: () => {
                                                                                setEditData(row)
                                                                                setShowConfirmationActiveUserModal(true)
                                                                            },
                                                                        },
                                                                    ]}
                                                                />
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={visibleColCount}>
                                                        <NoRecordFound />
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                    {isLoading && (
                                        <tr>
                                            <td colSpan={visibleColCount} className="text-center">
                                                <LoadingComponent />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            totalResult={data?.result?.totalRecords === 0 ? 0 : ((data?.result?.pageNumber - 1) * data?.result?.pageSize) + 1}
                            toResult={data?.result?.totalRecords === 0 ? 0 : Math.min(data?.result?.pageNumber * data?.result?.pageSize, data?.result?.totalRecords)}
                            ofResult={data?.result?.totalRecords}
                            onChange={handlePageSizeChange}
                            pageSize={pageSize}
                            onPageChange={handlePageClick}
                            pageCount={data?.result?.totalPages || 0}
                            currentPage={page}
                        />
                    </div>
                </div>
        </>
    )
}

export default AccountManager